import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, Users, ArrowLeft, RefreshCw, Search, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendanceCellRO = ({ value }) => {
  const getColor = (v) => {
    const u = (v || '').toUpperCase();
    if (u === '1') return 'bg-green-100 text-green-700 border-green-300';
    if (u === '0') return 'bg-red-100 text-red-700 border-red-300';
    if (u === 'J' || u === 'JO') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (u === 'R') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-white text-slate-400 border-slate-200';
  };
  return <div className={`w-10 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getColor(value)}`}>{value || '-'}</div>;
};

const TaskCellRO = ({ value }) => {
  const display = value !== '' && value !== undefined && value !== null ? `${value}%` : '-';
  const getColor = (v) => {
    if (v === '' || v === undefined || v === null) return 'bg-white text-slate-400';
    const n = parseInt(v, 10);
    if (isNaN(n)) return 'bg-white text-slate-400';
    if (n >= 80) return 'bg-green-100 text-green-700';
    if (n >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };
  return <div className={`w-14 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getColor(value)}`}>{display}</div>;
};

const OrientadorClasesPage = ({ user, onLogout }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetail, setClassDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('alumnos');
  const [searchTerm, setSearchTerm] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(`${API}/orientador/clases`, { headers });
        setClasses(res.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchClasses();
  }, []);

  const openClass = async (cls) => {
    setSelectedClass(cls);
    setDetailLoading(true);
    setActiveTab('alumnos');
    setSearchTerm('');
    try {
      const res = await axios.get(`${API}/orientador/clase/${cls.id}`, { headers });
      setClassDetail(res.data);
    } catch (e) { console.error(e); } finally { setDetailLoading(false); }
  };

  const groups = [...new Set(classes.map(c => c.group))].sort();

  const classesOfSelectedGroup = selectedGroup ? classes.filter(c => c.group === selectedGroup) : [];

  if (loading) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-teal-500" /></div>
    </Layout>
  );

  if (selectedClass && classDetail) {
    const students = classDetail.students || [];
    const attGrid = classDetail.attendance_grid || { columns: [], records: {} };
    const taskGrid = classDetail.task_grid || { columns: [], records: {} };
    const filteredStudents = students.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-4 md:p-6 lg:p-8">
          <Button variant="outline" onClick={() => { setSelectedClass(null); setClassDetail(null); }} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">{selectedClass.subject}</h1>
          <p className="text-sm text-slate-600 mb-4">Grupo: {selectedClass.group} | Prof. {selectedClass.teacher_name}</p>

          <div className="flex gap-0 mb-4">
            {['alumnos', 'asistencias', 'tareas'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${activeTab === tab ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {tab === 'alumnos' ? 'Alumnos' : tab === 'asistencias' ? 'Asistencias' : 'Tareas'}
              </button>
            ))}
          </div>

          {activeTab !== 'alumnos' && (
            <div className="relative mb-4 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar alumno..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
              {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
            </div>
          )}

          <Card className="p-4">
            {detailLoading ? (
              <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-teal-500" /></div>
            ) : activeTab === 'alumnos' ? (
              <div>
                <h2 className="text-lg font-semibold mb-3">Lista de Alumnos ({students.length})</h2>
                <div className="space-y-1">
                  {students.map((s, i) => (
                    <div key={s.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                      <span className="text-sm text-slate-400 w-6 text-right">{i + 1}.</span>
                      <span className="text-sm font-medium">{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeTab === 'asistencias' ? (
              <div>
                <h2 className="text-lg font-semibold mb-3">Registro de Asistencias</h2>
                {attGrid.columns.length === 0 ? <p className="text-slate-500 text-center py-8">Sin registros</p> : (
                  <div className="overflow-x-auto">
                    <table className="border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-2 text-xs font-semibold border-r-2 min-w-[140px] max-w-[140px]">Alumno</th>
                          {attGrid.columns.map((col, i) => (
                            col.isSeparator ?
                              <th key={i} className="text-center py-1 px-0 text-[8px] font-bold text-purple-600 border-r min-w-[28px] bg-purple-100">{col.label}</th>
                            : <th key={i} className="text-center py-2 px-1 text-xs border-r min-w-[44px] bg-slate-50">{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map(s => {
                          const rec = attGrid.records[s.id] || {};
                          return (
                            <tr key={s.id} className="border-b hover:bg-slate-50">
                              <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium border-r-2 min-w-[140px] max-w-[140px]">{s.name}</td>
                              {attGrid.columns.map((col, ci) => {
                                if (col.isSeparator) return <td key={ci} className="border-r bg-purple-50"></td>;
                                const v = rec[col.id || col.date] || '';
                                return <td key={ci} className="text-center py-1 px-0 border-r"><AttendanceCellRO value={v} /></td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 className="text-lg font-semibold mb-3">Registro de Tareas</h2>
                {taskGrid.columns.length === 0 ? <p className="text-slate-500 text-center py-8">Sin tareas</p> : (
                  <div className="overflow-x-auto">
                    <table className="border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-2 text-xs font-semibold border-r-2 min-w-[140px] max-w-[140px]">Alumno</th>
                          {taskGrid.columns.map((col, i) => (
                            col.isSeparator ?
                              <th key={i} className="text-center py-1 px-0 text-[8px] font-bold text-purple-600 border-r min-w-[28px] bg-purple-100">{col.label}</th>
                            : <th key={i} className="text-center py-2 px-1 text-xs border-r min-w-[55px] bg-slate-50">
                                <div className="font-bold">{col.number}</div>
                                <div className="text-slate-500 text-[10px]">{col.date ? col.date.split('-').reverse().slice(0, 2).join('/') : ''}</div>
                              </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map(s => {
                          const rec = taskGrid.records[s.id] || {};
                          return (
                            <tr key={s.id} className="border-b hover:bg-slate-50">
                              <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium border-r-2 min-w-[140px] max-w-[140px]">{s.name}</td>
                              {taskGrid.columns.map((col, ci) => {
                                if (col.isSeparator) return <td key={ci} className="border-r bg-purple-50"></td>;
                                return <td key={ci} className="text-center py-1 px-0 border-r"><TaskCellRO value={rec[col.id] || ''} /></td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8">
        {selectedGroup ? (
          <>
            <Button variant="outline" onClick={() => setSelectedGroup(null)} className="mb-4" data-testid="ori-back-to-groups">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Grupos
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="ori-group-classes-title">Grupo {selectedGroup}</h1>
            <p className="text-sm text-slate-600 mb-4">Clases del grupo (vista de solo lectura)</p>

            {classesOfSelectedGroup.length === 0 ? (
              <Card className="p-12 text-center">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Este grupo no tiene clases registradas</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classesOfSelectedGroup.map(cls => (
                  <Card key={cls.id} onClick={() => openClass(cls)}
                    data-testid={`ori-class-card-${cls.id}`}
                    className="p-5 bg-white border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="bg-teal-50 w-10 h-10 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-teal-500" />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 bg-slate-50 border px-2 py-0.5 rounded-full">{cls.group}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1 group-hover:text-teal-500">{cls.subject}</h3>
                    <p className="text-sm text-slate-600 mb-2">Prof. {cls.teacher_name}</p>
                    <div className="flex items-center text-sm text-slate-600">
                      <Users className="w-4 h-4 mr-1" /> {cls.student_ids?.length || 0} alumnos
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="ori-clases-title">Grupos de la Institución</h1>
            <p className="text-sm text-slate-600 mb-4">Selecciona un grupo para ver sus clases</p>

            {groups.length === 0 ? (
              <Card className="p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay grupos con clases registradas</p>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {groups.map(g => (
                  <Card key={g} onClick={() => setSelectedGroup(g)}
                    data-testid={`ori-group-card-${g}`}
                    className="p-6 bg-white border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer flex flex-col items-center justify-center text-center">
                    <div className="bg-teal-50 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                      <Users className="w-6 h-6 text-teal-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{g}</h3>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default OrientadorClasesPage;
